import React from 'react'
import { Box, Flex, Text } from '@workwhile/ui'
import { CopyableLink } from 'components/Shared/CopyableLink'
import {
  RelatedPayment,
  UserPaymentEventType,
  UserPaymentStatus,
} from 'api/payments/getUserPayments'
import { toTitleCase } from 'lib/string'
import { AmountRender } from './AmountRender'
import {
  BundledPaymentDetails,
  hasBundledPaymentDetails,
} from './BundledPaymentDetails'
import { getPayoutStatusLabel } from '../../utils/labels'

type BundledPaymentsAmountTableProps = {
  relatedPayments: RelatedPayment[]
  payoutAmount: number
  highlightPaymentId?: number
  payoutStatus?: UserPaymentStatus
}

const getPaymentTypeLabel = (payment: RelatedPayment): string => {
  const label = toTitleCase(payment.eventType)
  if (payment.eventSubType && !payment.shiftId) {
    return `${label} (${toTitleCase(payment.eventSubType)})`
  }
  return label
}

type RelatedPaymentGroup = {
  key: string
  shiftId: number | null
  payments: RelatedPayment[]
}

const groupRelatedPayments = (
  relatedPayments: RelatedPayment[]
): RelatedPaymentGroup[] => {
  const paymentsByShiftId = new Map<number, RelatedPayment[]>()

  for (const payment of relatedPayments) {
    if (payment.shiftId == null) {
      continue
    }

    const existing = paymentsByShiftId.get(payment.shiftId) ?? []
    existing.push(payment)
    paymentsByShiftId.set(payment.shiftId, existing)
  }

  const groups: RelatedPaymentGroup[] = []
  const seenShiftIds = new Set<number>()

  for (const payment of relatedPayments) {
    if (payment.shiftId != null) {
      if (seenShiftIds.has(payment.shiftId)) {
        continue
      }

      seenShiftIds.add(payment.shiftId)
      const shiftPayments = [
        ...(paymentsByShiftId.get(payment.shiftId) ?? []),
      ].sort((left, right) => {
        const leftIsShift =
          left.eventType === UserPaymentEventType.SHIFT ? 0 : 1
        const rightIsShift =
          right.eventType === UserPaymentEventType.SHIFT ? 0 : 1

        if (leftIsShift !== rightIsShift) {
          return leftIsShift - rightIsShift
        }

        return left.id - right.id
      })

      groups.push({
        key: `shift-${payment.shiftId}`,
        shiftId: payment.shiftId,
        payments: shiftPayments,
      })
      continue
    }

    groups.push({
      key: `payment-${payment.id}`,
      shiftId: null,
      payments: [payment],
    })
  }

  return groups
}

const AmountRow = ({
  highlighted = false,
  nested = false,
  amount,
  children,
}: {
  highlighted?: boolean
  nested?: boolean
  amount?: number
  children: React.ReactNode
}) => (
  <Flex
    justifyContent="space-between"
    alignItems="flex-start"
    gap={2}
    py={1}
    pr={2}
    pl={nested ? 4 : 2}
    backgroundColor={highlighted ? 'neutrals.50' : undefined}
  >
    <Box>{children}</Box>
    {amount == null ? null : <AmountRender value={amount} />}
  </Flex>
)

const PaymentAmountRow = ({
  payment,
  highlighted,
  nested,
}: {
  payment: RelatedPayment
  highlighted?: boolean
  nested?: boolean
}) => (
  <AmountRow
    highlighted={highlighted}
    nested={nested}
    amount={Number(payment.amount)}
  >
    {highlighted ? (
      <Text as="span" fontSize={1} fontWeight="bold" color="primary" mr={1}>
        Source ·
      </Text>
    ) : null}
    Payment{' '}
    <CopyableLink
      label={`${payment.id}`}
      link={`/user_payments?payment-id=${payment.id}`}
    />
    {' · '}
    Type{' '}
    <Text as="span" fontSize="sm">
      {getPaymentTypeLabel(payment)}
    </Text>
  </AmountRow>
)

export const BundledPaymentsAmountTable = ({
  relatedPayments,
  payoutAmount,
  highlightPaymentId,
  payoutStatus,
}: BundledPaymentsAmountTableProps) => {
  const bundledTotal = relatedPayments.reduce(
    (sum, payment) => sum + Number(payment.amount),
    0
  )
  const paymentGroups = groupRelatedPayments(relatedPayments)
  const payoutLabel = payoutStatus ? getPayoutStatusLabel(payoutStatus) : null

  return (
    <Box width="400px" maxWidth="100%">
      <Box maxHeight="320px" overflowY="auto">
        {paymentGroups.map((group, index) => (
          <Box
            key={group.key}
            borderBottom={
              index < paymentGroups.length - 1 ? '1px solid' : undefined
            }
            borderColor={
              index < paymentGroups.length - 1 ? 'neutrals.100' : undefined
            }
          >
            {group.shiftId != null ? (
              <ShiftPaymentGroup
                group={group}
                highlightPaymentId={highlightPaymentId}
              />
            ) : (
              <SinglePaymentGroup
                payment={group.payments[0]}
                highlightPaymentId={highlightPaymentId}
              />
            )}
          </Box>
        ))}
      </Box>

      <Box borderTop="1px solid" borderColor="neutrals.100">
        <AmountRow amount={bundledTotal}>
          <Text fontSize="sm" fontWeight="bold">
            Bundled total
          </Text>
        </AmountRow>
        <AmountRow amount={payoutAmount} highlighted>
          <Text fontSize="sm" fontWeight="bold">
            {payoutLabel ?? 'Payout'}
          </Text>
        </AmountRow>
      </Box>
    </Box>
  )
}

const ShiftPaymentGroup = ({
  group,
  highlightPaymentId,
}: {
  group: RelatedPaymentGroup
  highlightPaymentId?: number
}) => {
  const shiftContextPayment =
    group.payments.find(
      (payment) => payment.eventType === UserPaymentEventType.SHIFT
    ) ?? group.payments[0]
  const isGroupHighlighted = group.payments.some(
    (payment) => payment.id === highlightPaymentId
  )
  const shiftDetailProps = {
    ...shiftContextPayment,
    showShiftContext: true,
  }

  return (
    <>
      <AmountRow highlighted={isGroupHighlighted}>
        {isGroupHighlighted ? (
          <Text as="span" fontSize={1} fontWeight="bold" color="primary" mr={1}>
            Source ·
          </Text>
        ) : null}
        <Text as="span" fontSize="sm" fontWeight="bold">
          Shift{' '}
          <CopyableLink
            label={`${group.shiftId}`}
            link={`/shifts/${group.shiftId}`}
          />
        </Text>
      </AmountRow>
      {hasBundledPaymentDetails(shiftDetailProps) ? (
        <AmountRow highlighted={isGroupHighlighted} nested>
          <BundledPaymentDetails {...shiftDetailProps} />
        </AmountRow>
      ) : null}
      {group.payments.map((payment) => (
        <PaymentAmountRow
          key={payment.id}
          payment={payment}
          nested
          highlighted={payment.id === highlightPaymentId}
        />
      ))}
    </>
  )
}

const SinglePaymentGroup = ({
  payment,
  highlightPaymentId,
}: {
  payment: RelatedPayment
  highlightPaymentId?: number
}) => {
  const isHighlighted = payment.id === highlightPaymentId
  const detailProps = {
    ...payment,
    showShiftContext: true,
  }

  return (
    <>
      <PaymentAmountRow payment={payment} highlighted={isHighlighted} />
      {hasBundledPaymentDetails(detailProps) ? (
        <AmountRow highlighted={isHighlighted}>
          <BundledPaymentDetails {...detailProps} />
        </AmountRow>
      ) : null}
    </>
  )
}
